import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * ComplianceCard
 * Displays a single compliance metric with icon, label, count, status colour, and navigation.
 *
 * statusColor: 'red' | 'amber' | 'green'
 */
const ComplianceCard = ({ icon, label, count, statusColor, navigateTo, loading }) => {
  const navigate = useNavigate();

  const colorMap = {
    red: {
      bg: 'bg-red-50',
      icon: 'text-red-600',
      badge: 'bg-red-100 text-red-700',
      border: 'border-red-200 hover:border-red-400',
      count: 'text-red-700',
      dot: 'bg-red-500',
    },
    amber: {
      bg: 'bg-amber-50',
      icon: 'text-amber-600',
      badge: 'bg-amber-100 text-amber-700',
      border: 'border-amber-200 hover:border-amber-400',
      count: 'text-amber-700',
      dot: 'bg-amber-500',
    },
    green: {
      bg: 'bg-emerald-50',
      icon: 'text-emerald-600',
      badge: 'bg-emerald-100 text-emerald-700',
      border: 'border-emerald-200 hover:border-emerald-400',
      count: 'text-emerald-700',
      dot: 'bg-emerald-500',
    },
  };

  const colors = colorMap?.[statusColor] || colorMap?.green;

  const handleClick = () => {
    if (navigateTo) navigate(navigateTo);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-5 animate-pulse">
        <div className="flex items-start justify-between mb-4">
          <div className="w-11 h-11 rounded-xl bg-muted" />
          <div className="w-16 h-5 rounded-full bg-muted" />
        </div>
        <div className="w-12 h-8 rounded bg-muted mb-2" />
        <div className="w-3/4 h-4 rounded bg-muted" />
      </div>
    );
  }

  const isAlert = count > 0;
  const resolvedColor = isAlert ? statusColor : 'green';
  const resolvedColors = colorMap?.[resolvedColor] || colorMap?.green;

  return (
    <button
      onClick={handleClick}
      className={`bg-card rounded-xl border shadow-elevation-1 p-5 text-left w-full transition-all duration-200 hover:shadow-elevation-2 cursor-pointer group ${
        isAlert ? resolvedColors?.border : 'border-border hover:border-accent/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
          isAlert ? resolvedColors?.bg : 'bg-muted'
        }`}>
          <Icon
            name={icon}
            size={22}
            className={isAlert ? resolvedColors?.icon : 'text-muted-foreground'}
          />
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
          isAlert ? resolvedColors?.badge : 'bg-emerald-100 text-emerald-700'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            isAlert ? resolvedColors?.dot : 'bg-emerald-500'
          }`} />
          {isAlert ? (statusColor === 'red' ? 'Critical' : statusColor === 'amber' ? 'Warning' : 'Alert') : 'OK'}
        </span>
      </div>

      <p className={`text-3xl font-bold mb-1 ${
        isAlert ? resolvedColors?.count : 'text-foreground'
      }`}>
        {count}
      </p>

      <p className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors leading-snug">
        {label}
      </p>

      <div className="mt-3 flex items-center gap-1 text-xs text-accent font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        <span>View details</span>
        <Icon name="ArrowRight" size={12} />
      </div>
    </button>
  );
};

export default ComplianceCard;
